import {
    AlertTriangle,
    ArrowRight,
    BadgeIndianRupee,
    CheckCircle2,
    ChevronLeft,
    CircleAlert,
    LoaderCircle,
    ReceiptText,
    UserX,
} from 'lucide-react'
import React, { useContext, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './ForecloseAdvanceScreen.module.css'

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

const formatINR = (amount) => {
    const num = parseFloat(amount)
    if (!isFinite(num)) return '—'
    return num.toLocaleString('en-IN', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    })
}

const ForecloseAdvanceScreen = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { advanceIdentifier } = useParams()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)
    const token = authCtx?.token

    const advance = location?.state?.advance || null

    const [closureNote, setClosureNote] = useState('')
    const [isForeclosing, setIsForeclosing] = useState(false)
    const [result, setResult] = useState(null)
    const [showConfirm, setShowConfirm] = useState(false)

    const outstanding = parseFloat(advance?.outstandingBalance || 0)
    const basicAmount = parseFloat(advance?.basicAmount || advance?.salaryBasic || 0)
    const previewSufficient = basicAmount > 0 && outstanding <= basicAmount
    const previewShortfall = basicAmount > 0 ? Math.max(0, outstanding - basicAmount) : null

    const executeForeclosure = async () => {
        setShowConfirm(false)
        try {
            setIsForeclosing(true)

            const response = await fetch(BASE_URL_API + 'forecloseStaffAdvance', {
                method: 'POST',
                body: JSON.stringify({
                    token,
                    storeIdentifier: activeStore.storeIdentifier,
                    advanceIdentifier,
                    foreclosedBy: authCtx?.staffIdentifier || authCtx?.authIdentifier || '',
                    closureNote: closureNote.trim(),
                }),
            })

            const respo = await response.json()

            if (respo?.status === 'success') {
                setResult(respo.response)
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            switch (respo?.errorCode) {
                case 'ADV_001':
                    toast.error('Advance not found.', { position: 'bottom-center' })
                    navigate(-1)
                    break
                case 'ADV_003':
                    toast.error(
                        `Cannot foreclose — advance is already "${respo.response?.currentStatus || 'updated'}".`,
                        { position: 'bottom-center' }
                    )
                    navigate(-1)
                    break
                default:
                    toast.error(respo?.message || 'Foreclosure failed.', {
                        position: 'bottom-center',
                    })
            }
        } catch {
            toast.error('Network error.', { position: 'bottom-center' })
        } finally {
            setIsForeclosing(false)
        }
    }

    if (!advanceIdentifier) {
        return (
            <div className={styles.page}>
                <div className={styles.alertCard}>
                    <AlertTriangle size={16} />
                    <div>
                        <strong>No advance selected.</strong>
                        <p>Open this screen from an active advance card.</p>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className={styles.page}>
            {/* ── Header ── */}
            <div className={styles.header}>
                <button
                    className={styles.backBtn}
                    type="button"
                    onClick={() => navigate(-1)}
                    disabled={isForeclosing}
                >
                    <ChevronLeft size={18} />
                </button>
                <div className={styles.headerMeta}>
                    <span className={styles.heroPill}>
                        <UserX size={12} />
                        Staff Exit Action
                    </span>
                    <h1 className={styles.headerTitle}>Foreclose Advance</h1>
                    <p className={styles.headerSub}>
                        for <strong>{advance.staffName || 'Staff'}</strong>
                    </p>
                </div>
            </div>

            {/* ── Result card (shown after success) ── */}
            {result ? (
                <div className={styles.resultCard}>
                    <div className={styles.resultHeader}>
                        <CheckCircle2 size={20} className={styles.resultIcon} />
                        <span>Advance Foreclosed</span>
                    </div>

                    <div className={styles.resultGrid}>
                        <div className={styles.resultCell}>
                            <span>Staff</span>
                            <strong>{result.staffName || advance.staffName}</strong>
                        </div>
                        <div className={styles.resultCell}>
                            <span>Outstanding at Foreclosure</span>
                            <strong>₹{formatINR(result.outstandingAtForeclosure)}</strong>
                        </div>
                        <div className={styles.resultCell}>
                            <span>Final Payslip Deduction</span>
                            <strong>₹{formatINR(result.finalPayslipDeductionAmount)}</strong>
                        </div>
                        <div className={styles.resultCell}>
                            <span>Closed On</span>
                            <strong>{result.closedOn || '—'}</strong>
                        </div>
                    </div>

                    {result.finalPayslipSufficientForFullRecovery ? (
                        <div className={`${styles.sufficiencyBadge} ${styles.sufficientBadge}`}>
                            <CheckCircle2 size={14} />
                            <span>
                                Final payslip (~₹{formatINR(result.approximateFinalNetSalary)}) is
                                sufficient for full recovery.
                            </span>
                        </div>
                    ) : (
                        <div className={`${styles.sufficiencyBadge} ${styles.shortfallBadge}`}>
                            <CircleAlert size={14} />
                            <span>
                                Estimated shortfall: ₹{formatINR(result.estimatedShortfall)}. Payroll
                                engine will deduct as much as possible from the final payslip.
                            </span>
                        </div>
                    )}

                    <p className={styles.resultNote}>{result.message}</p>

                    <button
                        type="button"
                        className={styles.doneBtn}
                        onClick={() => navigate(-1)}
                    >
                        Done <ArrowRight size={14} />
                    </button>
                </div>
            ) : (
                <>
                    {/* ── Advance summary ── */}
                    <div className={styles.summaryCard}>
                        <div className={styles.summaryHead}>
                            <ReceiptText size={14} />
                            <span>Advance Summary</span>
                        </div>
                        <div className={styles.summaryGrid}>
                            <div className={styles.summaryCell}>
                                <span>Disbursed</span>
                                <strong>₹{formatINR(advance.disbursedAmount || advance.approvedAmount)}</strong>
                            </div>
                            <div className={styles.summaryCell}>
                                <span>Repaid So Far</span>
                                <strong>₹{formatINR(advance.totalRepaid)}</strong>
                            </div>
                        </div>
                        <div className={styles.outstandingRow}>
                            <BadgeIndianRupee size={15} />
                            <span>Outstanding Balance</span>
                            <strong className={styles.outstandingAmount}>
                                ₹{formatINR(outstanding)}
                            </strong>
                        </div>
                    </div>

                    {/* ── Payslip sufficiency preview ── */}
                    {basicAmount > 0 ? (
                        <div
                            className={
                                previewSufficient
                                    ? `${styles.previewBadge} ${styles.previewSufficient}`
                                    : `${styles.previewBadge} ${styles.previewShortfall}`
                            }
                        >
                            {previewSufficient ? (
                                <CheckCircle2 size={14} />
                            ) : (
                                <CircleAlert size={14} />
                            )}
                            <div>
                                <strong>
                                    Approx. final payslip: ₹{formatINR(basicAmount)}
                                </strong>
                                {previewSufficient ? (
                                    <p>Sufficient to cover the full outstanding balance.</p>
                                ) : (
                                    <p>
                                        Estimated shortfall: ₹{formatINR(previewShortfall)}. Payroll
                                        will deduct as much as possible.
                                    </p>
                                )}
                            </div>
                        </div>
                    ) : null}

                    {/* ── Closure note + foreclose button ── */}
                    <div className={styles.formCard}>
                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel} htmlFor="closureNote">
                                Closure Note{' '}
                                <span className={styles.optionalTag}>(optional)</span>
                            </label>
                            <textarea
                                id="closureNote"
                                className={styles.textArea}
                                rows={3}
                                value={closureNote}
                                placeholder="e.g. Staff resigned April 15. Final payslip in April cycle."
                                onChange={(e) => setClosureNote(e.target.value)}
                                disabled={isForeclosing}
                            />
                        </div>

                        <button
                            type="button"
                            className={styles.forecloseBtn}
                            onClick={() => setShowConfirm(true)}
                            disabled={isForeclosing}
                        >
                            {isForeclosing ? (
                                <>
                                    <LoaderCircle size={15} className={styles.spinIcon} />
                                    Processing…
                                </>
                            ) : (
                                <>
                                    Foreclose &amp; Set Final Deduction{' '}
                                    <ArrowRight size={14} />
                                </>
                            )}
                        </button>
                    </div>
                </>
            )}

            {/* ── Inline confirmation dialog ── */}
            {showConfirm ? (
                <div className={styles.overlay}>
                    <div className={styles.confirmDialog}>
                        <div className={styles.confirmIcon}>
                            <AlertTriangle size={22} />
                        </div>
                        <h2 className={styles.confirmTitle}>Foreclose Advance?</h2>
                        <p className={styles.confirmBody}>
                            ₹{formatINR(outstanding)} will be deducted from{' '}
                            <strong>{advance.staffName || 'this staff member'}</strong>'s final
                            payslip. This action cannot be undone.
                        </p>
                        <div className={styles.confirmActions}>
                            <button
                                type="button"
                                className={styles.cancelBtn}
                                onClick={() => setShowConfirm(false)}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className={styles.confirmBtn}
                                onClick={executeForeclosure}
                            >
                                Foreclose
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    )
}

export default ForecloseAdvanceScreen
